import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Clock, CalendarDays } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { resolveImage } from "../lib/api";
import { Reveal } from "../components/Reveal";
import RichText from "../components/RichText";
import Marquee from "../components/Marquee";

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Home = () => {
  const { content } = useContent();
  const h = content?.home || {};
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen pt-28 pb-40 md:pb-52 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-8 items-center">
          {/* Text */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <MaskLine delay={0.1}>
              <span className="uppercase tracking-[0.3em] text-xs md:text-sm text-dusty font-bold">{h.kicker}</span>
            </MaskLine>
            <h1 className="mt-4 font-serif leading-[1.02] text-navy">
              <MaskLine delay={0.2} className="text-6xl sm:text-7xl lg:text-[96px] tracking-tight">{h.nameLine1}</MaskLine>
              <MaskLine delay={0.32} className="text-4xl lg:text-5xl italic text-dusty pl-1">{h.amp}</MaskLine>
              <MaskLine delay={0.44} className="text-6xl sm:text-7xl lg:text-[96px] tracking-tight">{h.nameLine2}</MaskLine>
            </h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
              className="mt-6 text-navy max-w-md text-base md:text-lg"
            >
              {h.date}
            </motion.p>
          </div>

          {/* Portrait framed image */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto max-w-sm lg:max-w-none aspect-[3/4] overflow-hidden arch-top rounded-b-lg shadow-[0_30px_80px_rgba(38,62,76,0.18)]"
            >
              <motion.img
                style={{ y: imgY }}
                src={resolveImage(h.heroImageUrl)}
                alt="George and Amy"
                className="absolute inset-0 w-full h-[118%] object-cover"
              />
            </motion.div>
          </div>
        </div>

        {/* Highlighted info block, overlapping */}
        <Reveal
          delay={1}
          className="relative z-10 max-w-4xl mx-auto px-6 -mb-24 mt-14 md:mt-20"
        >
          <div className="bg-white rounded-xl border border-border shadow-[0_30px_70px_rgba(38,62,76,0.12)] p-8 md:p-10 grid sm:grid-cols-3 gap-8 sm:divide-x divide-border" data-testid="home-info-block">
            {[
              { icon: CalendarDays, label: "The Date", value: h.date },
              { icon: Clock, label: "The Time", value: h.time },
              { icon: MapPin, label: "The Place", value: h.location },
            ].map((it, i) => (
              <div key={i} className="sm:px-4 first:pl-0 text-center sm:text-left">
                <it.icon size={22} className="text-dusty mx-auto sm:mx-0 mb-3" />
                <div className="uppercase tracking-[0.18em] text-xs font-bold text-navy/50 mb-1.5">{it.label}</div>
                <div className="font-serif text-lg text-navy leading-snug">{it.value}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/details"
              className="inline-flex items-center gap-2 text-navy hover:text-dusty transition-colors group"
              data-testid="home-more-details"
            >
              <span className="uppercase tracking-[0.18em] text-sm border-b border-navy/30 group-hover:border-dusty pb-1">
                {h.moreDetailsLabel || "See all the details"}
              </span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </section>

      <div className="h-24" />
      <Marquee text={content?.settings?.coupleNames || "George & Amy"} />

      {/* Intro */}
      <section className="py-28 md:py-36">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <span className="uppercase tracking-[0.3em] text-xs text-dusty font-bold">A little note</span>
          </Reveal>
          <Reveal delay={0.1}>
            <RichText html={h.introHtml} className="mt-8 text-xl md:text-2xl font-serif italic text-navy leading-relaxed" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
